import React from 'react';
/* eslint-disable no-unused-vars */
import { motion } from 'framer-motion';
/* eslint-enable no-unused-vars */
import { ArrowRight, Shield, Heart, HandHeart, Zap, CheckCircle2, Sword, Siren, HeartPulse, Flame, Lock, LifeBuoy, Scale, Brain, Users, MapPin, Award, Compass, Ambulance } from 'lucide-react';
import { Link } from 'react-router-dom';
import { siteData } from '../data.js';
import SEO from '../components/SEO.jsx';
import { useCmsCollection, useCmsSettings } from '../lib/cms.js';
import CampaignBadge from '../components/CampaignBadge.jsx';

const HandsFormingHeart = (props) => (
  <img
    src="https://assets.auxodev.dk/cdn-cgi/image/width=160,quality=90,format=auto/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/hands-heart.png"
    alt="Hænder der danner et hjerte"
    className={`${props.className || ''} object-contain brightness-0 invert`}
  />
);

const groupIcons = {
  Shield,
  Siren,
  HeartPulse,
  Flame,
  Lock,
  Ambulance
};

export default function Home() {
  const [openIndex, setOpenIndex] = React.useState(null);
  const currentSiteData = useCmsSettings(siteData);

  const defaultFaqs = [
    {
      q: "Hvem kan få hjælp fra TFDI?",
      a: "TFDI hjælper veteraner, forsvarsansatte og frontpersonel samt deres familier, som er ramt af arbejdsrelaterede belastningsreaktioner – fysisk, psykisk og kognitivt. Vi arbejder målrettet for, at ingen falder mellem systemerne på grund af faggruppe, geografi eller myndighedsgrænser."
    },
    {
      q: "Hvilken type støtte tilbyder I?",
      a: "TFDI yder Peer-Peer mentor ordning, dækningstilsagn til behandlingsforløb samt fremlægger vores målgruppers sager for politiske beslutningstagere samt i udvalget for en mere sammenhængende veteranindsats."
    },
    {
      q: "Er jeres tjenester gratis?",
      a: "Ja, vores rådgivning, vejledning og dækningstilsagn til behandlingsforløb, ved godkendelse af ansøgning, er gratis for dem af vores målgruppe som henvender sig. TFDI medlemskab koster 50 kr. årligt og er for alle som ønsker at støtte TFDI mission, formål og mål."
    },
    {
      q: "Hvordan kontakter jeg jer for hjælp?",
      a: (
        <span>
          Du kan kontakte os via vores kontaktformular på hjemmesiden, eller e-mail{" "}
          <a href="mailto:info@takfordinindsats.dk" className="text-red-600 hover:underline font-semibold">info@takfordinindsats.dk</a>{" "}
          ved generel henvendelse og{" "}
          <a href="mailto:behandling@takfordinindsats.dk" className="text-red-600 hover:underline font-semibold">behandling@takfordinindsats.dk</a>{" "}
          ved ansøgning om dækningstilsagn til et behandlingsforløb. Du er også velkommen til at kontakte vores formand på{" "}
          <a href="mailto:formand@takfordinindsats.dk" className="text-red-600 hover:underline font-semibold">formand@takfordinindsats.dk</a>{" "}
          ved behov.
        </span>
      )
    },
    {
      q: "Hvor hurtigt kan I hjælpe i akutte situationer?",
      a: "Vi besvarer din henvendelse hurtigst muligt. Vi skal gøre opmærksom på at TFDI er en frivillig almennyttig forening, som er drevet af frivillige. TFDI afholder bestyrelsesmøder hver anden måned, hvor indsendte ansøgninger behandles når de har været behandlet af TFDI behandlingsudvalg."
    }
  ];

  const { data: rawFaq } = useCmsCollection('faq', []);
  const faqs = (rawFaq && rawFaq.length > 0)
    ? rawFaq.map(f => {
        const d = f.data || f;
        return {
          q: d.spoergsmaal || d.q,
          a: d.svar ? (
            <div 
              className="prose prose-sm prose-slate max-w-none text-slate-600 leading-relaxed [&_a]:text-red-600 [&_a]:hover:underline [&_a]:font-semibold"
              dangerouslySetInnerHTML={{ __html: d.svar }} 
            />
          ) : d.a,
        };
      })
    : defaultFaqs;

  const { data: rawWhoWeHelp } = useCmsCollection('hvem_hjaelper_vi', siteData.whoWeHelp || []);
  const whoWeHelpList = rawWhoWeHelp.map(g => {
    const d = g.data || g;
    return {
      name: d.navn || d.name,
      icon: groupIcons[d.ikon || d.icon] || Shield,
    };
  });

  return (
    <div className="flex flex-col">
      <SEO
        title="Tak For Din Indsats | Støtte til Danmarks frontpersonale"
        description="Tak For Din Indsats (TFDI) er en frivillig forening, der skaber et trygt sikkerhedsnet med mentorordning, rådgivning og behandlingsstøtte til Danmarks frontpersonale."
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/80 to-transparent z-10" />
          <img 
            src={currentSiteData.hero.images[0]} 
            alt="Hero background" 
            className="w-full h-full object-cover opacity-60 scale-105 animate-pulse-slow"
          />
        </div>

        {/* Aktuel kampagne-cirkel (Desktop: rykket indad mod midten, men med god balance) */}
        <div className="hidden lg:block absolute left-24 xl:left-36 2xl:left-48 top-40 xl:top-44 z-30">
          <CampaignBadge />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
            {/* Aktuel kampagne-cirkel (Mobil/Tablet: centreret øverst før titlen) */}
            <CampaignBadge className="block lg:hidden mb-6 z-30" />

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center"
            >
              <span className="font-handwriting text-3xl md:text-5xl text-red-500 font-normal mb-3 block tracking-wide select-none">
                Velkommen til foreningen
              </span>
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white leading-none mb-6 whitespace-nowrap">
                TAK FOR DIN INDSATS
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-slate-300 max-w-3xl mx-auto mb-2 leading-relaxed">
                Vi er en frivillig forening, som hjælper alt frontpersonale der arbejder for Danmark
              </p>
              <p className="font-handwriting text-2xl md:text-3xl text-red-400 font-normal mb-10 tracking-wide select-none">
                Samt deres pårørende og familie
              </p>

              {/* De 6 faggrupper */}
              <div className="w-full max-w-5xl mb-12">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-5">
                  {whoWeHelpList.map((group, idx) => {
                    const Icon = group.icon;
                    return (
                      <div 
                        key={idx} 
                        className="flex flex-col items-center justify-center p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.08] hover:border-red-500/30 hover:shadow-[0_0_25px_rgba(239,68,68,0.2)] transition-all duration-300 group"
                      >
                        <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4 group-hover:bg-red-500/20 transition-colors">
                          <Icon className="w-7 h-7 text-red-500 group-hover:scale-110 transition-transform" />
                        </div>
                        <span className="text-sm md:text-base font-semibold text-slate-100 text-center tracking-wide">{group.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to={siteData.hero.ctaPrimary.href}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-red-600 text-white rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-xl shadow-red-600/30 group"
                >
                  {siteData.hero.ctaPrimary.text}
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a 
                  href={siteData.hero.ctaSecondary.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#db9655] hover:bg-[#c98545] text-white rounded-full font-bold text-lg transition-all shadow-xl shadow-[#db9655]/30"
                >
                  {siteData.hero.ctaSecondary.text}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ny formål og vision sektion med billede til højre og tekst til venstre */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Tekst til venstre */}
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                Vores formål og vision
              </h2>
              
              <div className="space-y-4 text-slate-600">
                <p className="text-lg font-semibold text-slate-900 leading-relaxed">
                  Skal alle, som yder en særlig indsats for Danmark og os danskere, ikke sikres et tilstrækkeligt sikkerhedsnet?
                </p>
                
                <div className="flex gap-3 items-start">
                  <span className="mt-[9px] w-2 h-2 rounded-full bg-red-600 shrink-0" />
                  <p className="text-base leading-relaxed">
                    Arbejdsrelaterede belastningsreaktioner kan ramme både fysisk, psykisk og kognitivt – og konsekvenserne kan være alvorlige for det enkeles menneske, familien og arbejdslivet.
                  </p>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="mt-[9px] w-2 h-2 rounded-full bg-red-600 shrink-0" />
                  <p className="text-base leading-relaxed">
                    I dag oplever mange veteraner, forsvarsansatte og frontpersonel ikke samme adgang til støtte og hjælp, afhængigt af om de arbejder inden for <span className="text-red-600 font-semibold">Forsvar, Politi, Sygehus, Fængsel, Brand/Redning eller Ambulance</span>.
                  </p>
                </div>

                <div className="flex gap-3 items-start">
                  <span className="mt-[9px] w-2 h-2 rounded-full bg-red-600 shrink-0" />
                  <p className="text-base leading-relaxed">
                    Alle, der arbejder for vores fælles sikkerhed og samfund, bør have lige adgang til hjælp, støtte og behandling.
                  </p>
                </div>

                <p className="text-lg font-bold text-red-600 pt-2 leading-relaxed">
                  Det arbejder Tak For Din Indsats for at ændre.
                </p>
              </div>

              <div className="pt-4">
                <Link 
                  to="/om-os" 
                  className="inline-flex items-center justify-center px-6 py-3.5 border border-transparent text-base font-bold rounded-xl text-white bg-red-600 hover:bg-red-700 transition-colors shadow-lg shadow-red-600/10 gap-2 group"
                >
                  Læs mere om os
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Billede til højre med stempel ovenpå i hjørnet */}
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-red-100/50 -rotate-2 scale-105 -z-10" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-100 group">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/danish-six-professions-authentic-uniforms.png" 
                  alt="Vores formål og vision - De 6 faggrupper" 
                  className="w-full h-[450px] sm:h-[550px] md:h-[650px] lg:h-[700px] object-cover group-hover:scale-102 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/10 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Stempel mærke - placeret som et autentisk kvalitets-/infostempel på sektionen */}
              <div className="absolute -top-6 -right-4 sm:-top-8 sm:-right-6 z-20 pointer-events-auto">
                <div className="rotate-[7deg] hover:rotate-0 transition-transform duration-300 cursor-default select-none drop-shadow-xl">
                  <div className="p-3.5 rounded-full w-36 h-36 sm:w-40 sm:h-40 flex flex-col items-center justify-center text-center border-2 border-dashed border-red-500 bg-white/95 backdrop-blur-md shadow-lg text-slate-800">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-red-600 text-white font-extrabold text-[9px] sm:text-[10px] tracking-wider uppercase mb-1 shadow-sm text-center">
                      NY hjemmeside 😊
                    </span>
                    <p className="text-[9.5px] sm:text-[10.5px] leading-snug font-semibold text-slate-700 text-center max-w-[125px] sm:max-w-[135px] mx-auto my-auto flex items-center justify-center">
                      Vi er SÅ glade, men tager forbehold for fejl. Skriv gerne hvis du finder noget 😊 Tak!
                    </p>
                    <span className="text-[9px] text-red-600 mt-1 uppercase tracking-widest font-bold text-center block">
                      ★ TFDI ★
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section (Højere oppe) */}
      <section className="w-full bg-red-600 py-20 md:py-24 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-red-500 rounded-full -mr-32 -mt-32 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-700 rounded-full -ml-32 -mb-32 opacity-50" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">Har du brug for hjælp?</h2>
            <p className="text-xl text-red-100 mb-12">
              Kontakt os i dag og få den rådgivning og støtte du har brug for.
            </p>
            <div className="flex justify-center">
              <Link to="/kontakt" className="px-10 py-4 bg-white text-red-600 rounded-full font-extrabold text-lg hover:bg-slate-50 transition-all shadow-xl">
                Kontakt os i dag
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vores formål og vision */}
      <section className="relative py-24 overflow-hidden w-full">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/danish-six-professions-sunset-solidarity.png" 
            alt="Baggrund" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-[2px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-3xl mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              TFDI's mål
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              Vi arbejder målrettet på at sikre et stærkt, retfærdigt og helhedsorienteret sikkerhedsnet for dem, der yder en særlig indsats for Danmark.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Ligelig adgang til hjælp",
                desc: "Ligelig adgang til hjælp og behandling for veteraner, forsvarsansatte og frontpersonel.",
                icon: Scale,
                color: "bg-red-50 text-red-600 border-red-100"
              },
              {
                title: "Helhedsorienterede indsatser",
                desc: "Helhedsorienterede indsatser, der omfatter både fysiske, psykiske og kognitive følger.",
                icon: Brain,
                color: "bg-red-50 text-red-600 border-red-100"
              },
              {
                title: "Støtte til pårørende & børn",
                desc: "Bedre støtte til pårørende og børn.",
                icon: Users,
                color: "bg-red-50 text-red-600 border-red-100"
              },
              {
                title: "Forebyggelse af kriser",
                desc: "Forebyggelse af isolation, misbrug og selvmord.",
                icon: LifeBuoy,
                color: "bg-red-50 text-red-600 border-red-100"
              },
              {
                title: "Ensartet hjælp på tværs",
                desc: "Ensartet hjælp på tværs af kommuner og myndigheder.",
                icon: MapPin,
                color: "bg-red-50 text-red-600 border-red-100"
              },
              {
                title: "Nye behandlingsmuligheder",
                desc: "Anerkendelse af nye og dokumenterede behandlingsmuligheder.",
                icon: Award,
                color: "bg-red-50 text-red-600 border-red-100"
              },
              {
                title: "Fokus på de mest udsatte",
                desc: "Fokus på de mest udsatte, som ikke selv kan navigere i systemet.",
                icon: Compass,
                color: "bg-red-50 text-red-600 border-red-100",
                span: "lg:col-span-3",
                isFeatured: true
              }
            ].map((goal, idx) => {
              const IconComponent = goal.icon;
              if (goal.isFeatured) {
                return (
                  <div 
                    key={idx} 
                    className={`p-6 md:p-8 rounded-3xl border border-red-500/20 bg-gradient-to-r from-slate-900/90 to-red-950/20 hover:shadow-2xl hover:border-red-500/40 transition-all duration-300 group flex flex-col md:flex-row md:items-center justify-between gap-6 ${goal.span || ""}`}
                  >
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-red-600 text-white border border-red-600 shrink-0 shadow-md shadow-red-600/10 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent size={28} />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-red-400 uppercase tracking-wider">Særligt fokus</span>
                        <h3 className="font-extrabold text-white text-xl md:text-2xl">
                          {goal.title}
                        </h3>
                      </div>
                    </div>
                    <div className="md:max-w-xl">
                      <p className="text-slate-300 text-base md:text-lg leading-relaxed font-medium">
                        {goal.desc}
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <div 
                  key={idx} 
                  className="flex gap-4 p-6 rounded-2xl border border-slate-800 bg-slate-900/80 hover:border-red-500/30 hover:shadow-xl hover:shadow-[0_8px_30px_rgba(239,68,68,0.05)] transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-red-500/20 bg-red-500/10 text-red-400 shrink-0 transition-all duration-300 group-hover:bg-red-600 group-hover:text-white group-hover:border-red-600 group-hover:scale-110">
                    <IconComponent size={22} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-white text-base md:text-lg group-hover:text-red-400 transition-colors">
                      {goal.title}
                    </h3>
                    <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                      {goal.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center mt-12">
            <Link 
              to="/om-os" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white font-bold rounded-full hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 group"
            >
              Læs mere om vores formål og vision
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-12 -left-12 w-64 h-64 bg-red-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl">
                <img 
                  src={currentSiteData.hero.images[2] || siteData.hero.images[2]} 
                  alt="Vores vision" 
                  className="w-full h-[450px] md:h-[550px] object-cover"
                />
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">{currentSiteData.vision.title}</h2>
              <div className="space-y-6">
                {currentSiteData.vision.content.map((p, i) => (
                  <p key={i} className="text-lg text-slate-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>

              {/* Citat integreret direkte i sektionen */}
              <div className="border-l-4 border-red-600 pl-6 my-8 italic">
                <blockquote className="text-lg text-slate-700 font-medium leading-relaxed mb-2">
                  ”Som tidligere soldat ved jeg, at den mentale sundhed er afgørende for at kunne fungere i hverdagen. TFDI giver den støtte, som many har brug for.”
                </blockquote>
                <cite className="not-italic text-sm text-slate-500 block font-semibold">
                  — Steffen Carlsen, <span className="text-red-600">Bestyrelsesmedlem og medstifter, tidligere jægersoldat</span>
                </cite>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vil du vide mere? */}
      <div className="relative py-24 overflow-hidden bg-slate-900">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{ backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/danish-front-personnel-solidarity-wide.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/80 to-slate-950/50" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Vil du vide mere?</h2>
          <p className="text-slate-300 mb-8 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Kontakt os i dag og hør mere om vores arbejde og hvordan du kan bidrage.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/kontakt" 
              className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 rounded-full font-bold hover:bg-slate-100 transition-all duration-300 shadow-lg shadow-white/10 hover:scale-105"
            >
              Kontakt os
            </Link>
            <Link 
              to="/kontakt" 
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-slate-900 transition-all duration-300 hover:scale-105"
            >
              Bliv frivillig
            </Link>
          </div>
        </div>
      </div>

      {/* Hvordan kan du gøre en forskel? */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Hvordan kan du gøre en forskel?</h2>
            <p className="text-slate-600 text-lg">
              Sig Tak For Din Indsats til dem, som dagligt redder Danmark, ved at sikre disse en rettidig og helhedsorienteret indsats.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Kort 1: Bliv medlem */}
            <div className="flex flex-col items-center text-center group">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Bliv medlem</h3>
              <p className="text-slate-600 leading-relaxed mb-6 max-w-sm">
                Som medlem støtter du vores arbejde og får adgang til eksklusive fordele og arrangementer.
              </p>
              <a 
                href="https://tilmeld.takfordinindsats.dk/signup" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-red-600 font-bold hover:text-red-700 transition-colors group/link"
              >
                Bliv medlem <ArrowRight size={18} className="group-hover/link:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Kort 2: Donér til sagen */}
            <div className="flex flex-col items-center text-center group">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <circle cx="12" cy="12" r="3" />
                  <text x="12" y="14.5" fontSize="7" fontWeight="bold" textAnchor="middle" fill="currentColor" stroke="none">kr</text>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Donér til sagen</h3>
              <p className="text-slate-600 leading-relaxed mb-6 max-w-sm">
                Din donation gør en direkte forskel for mennesker i krise og hjælper os med at udvide vores arbejde.
              </p>
              <Link 
                to="/stoet-os" 
                className="inline-flex items-center gap-2 text-red-600 font-bold hover:text-red-700 transition-colors group/link"
              >
                Donér nu <ArrowRight size={18} className="group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Kort 3: Få hjælp i dag */}
            <div className="flex flex-col items-center text-center group">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Få hjælp i dag</h3>
              <p className="text-slate-600 leading-relaxed mb-6 max-w-sm">
                Har du brug for hjælp? Kontakt os og find ud af hvordan vi kan støtte dig.
              </p>
              <Link 
                to="/kontakt" 
                className="inline-flex items-center gap-2 text-red-600 font-bold hover:text-red-700 transition-colors group/link"
              >
                Kontakt os <ArrowRight size={18} className="group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full bg-red-600 py-20 md:py-24 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-red-500 rounded-full -mr-32 -mt-32 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-700 rounded-full -ml-32 -mb-32 opacity-50" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">Har du brug for hjælp?</h2>
            <p className="text-xl text-red-100 mb-12">
              Kontakt os i dag og få den rådgivning og støtte du har brug for.
            </p>
            <div className="flex justify-center">
              <Link to="/kontakt" className="px-10 py-4 bg-white text-red-600 rounded-full font-extrabold text-lg hover:bg-slate-50 transition-all shadow-xl">
                Kontakt os i dag
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Left Column */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              <span className="text-red-600 font-bold text-sm uppercase tracking-wider mb-3">FAQs</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
                Her finder du svar på de mest almindelige spørgsmål om TFDI og vores arbejde.
              </h2>
              <p className="text-slate-600 mb-8 text-lg">
                Kan du ikke finde det svar, du leder efter? Du er altid velkommen til at kontakte os direkte.
              </p>
              <div>
                <Link 
                  to="/kontakt" 
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-900 text-slate-900 font-bold rounded-full hover:bg-slate-900 hover:text-white transition-all"
                >
                  Kontakt TFDI
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-7 space-y-2">
              {faqs.map((faq, index) => {
                const isOpen = openIndex === index;
                return (
                  <div 
                    key={index} 
                    className="border-b border-slate-200 transition-all duration-300"
                  >
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className="w-full flex items-center justify-between py-5 text-left font-bold text-slate-900 hover:text-red-600 transition-colors focus:outline-none cursor-pointer"
                    >
                      <span className="text-lg pr-4">{faq.q}</span>
                      <span className={`shrink-0 w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 transition-transform duration-300 ${isOpen ? 'rotate-180 text-red-600 border-red-200 bg-red-50' : 'bg-white'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>
                    <div 
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[500px]' : 'max-h-0'}`}
                    >
                      <div className="pb-6 pt-1 text-slate-600 leading-relaxed">
                        {faq.a}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Concluding Section: Vores formål og vision */}
      <section className="relative py-28 overflow-hidden bg-slate-950">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/512cb13c-28f0-4ecb-9d9d-4ac03cf77200/chatgpt-image-9-jun-2026-09-49-29.png" 
            alt="Vores formål og vision - De 6 faggrupper i silhuet" 
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/30 to-slate-950/90" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Block */}
          <div className="text-left max-w-3xl mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-wide uppercase text-white mb-8">
              VORES FORMÅL OG MÅL
            </h2>
            
            <div className="space-y-4 max-w-2xl">
              <p className="text-base md:text-lg text-slate-300 font-light leading-relaxed">
                Tak For Din Indsats arbejder for veteraner, forsvarsansatte og frontpersonel, som er ramt av arbejdsrelaterede belastningsreaktioner – fysisk, psykisk og kognitivt.
              </p>
              <p className="text-base md:text-lg text-slate-300 font-light leading-relaxed">
                Vi arbejder for et stærkere og mere ligeligt sikkerhedsnet, hvor ingen står alene med følgerne af deres indsats for Danmark og os danskere.
              </p>
            </div>
          </div>

          {/* Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pt-10">
            {[
              {
                title: "HJÆLP OG STØTTE",
                desc: "Vi hjælper mennesker og familier, når belastningerne bliver for store, og når overskuddet til selv at navigere i systemet mangler.",
                color: "#a81c20",
                icon: HandsFormingHeart
              },
              {
                title: "HELHEDSORIENTERET INDSATS",
                desc: "Vi arbejder for helhedsorienterede løsninger med fokus på både fysiske, psykiske og kognitive belastningsreaktioner.",
                color: "#1c4b82",
                icon: Brain
              },
              {
                title: "PÅRØRENDE OG FAMILIER",
                desc: "Belastninger rammer hele familien. Derfor arbejder vi også for støtte til pårørende, børn og nære relationer.",
                color: "#256a37",
                icon: Users
              },
              {
                title: "LIGELIG ADGANG TIL HJÆLP",
                desc: "Alle veteraner, forsvarsansatte og frontpersonel skal have lige adgang til støtte, behandling og hjælp – uanset faggruppe og bopæl.",
                color: "#b38b2b",
                icon: Scale
              }
            ].map((card, idx) => {
              const Icon = card.icon;
              return (
                <div 
                  key={idx} 
                  className="relative flex flex-col items-center justify-between pt-16 pb-12 px-6 bg-slate-900/80 border border-slate-800 rounded-2xl shadow-xl min-h-[420px] backdrop-blur-sm transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-2xl hover:bg-slate-900/95 hover:border-slate-700 group"
                >
                  {/* Inner overflow container for watermark to prevent clipping of the top badge */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
                    {/* Low-Opacity Watermark Graphic (Bottom) */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-32 h-32 opacity-5">
                      <Icon className="w-full h-full text-white" />
                    </div>
                  </div>

                  {/* Floating Circle Icon Badge */}
                  <div 
                    className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full flex items-center justify-center border-4 border-slate-950 shadow-lg text-white transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: card.color }}
                  >
                    <Icon className="w-10 h-10" />
                  </div>

                  {/* Content Block */}
                  <div className="flex flex-col items-center w-full z-10">
                    <h3 className="text-lg font-bold tracking-wider uppercase text-center text-white mb-3">
                      {card.title}
                    </h3>
                    
                    {/* Semantic Color Divider */}
                    <div 
                      className="w-10 h-1 rounded-full mb-6"
                      style={{ backgroundColor: card.color }}
                    />

                    <p className="text-sm md:text-base text-slate-300 text-center leading-relaxed font-light">
                      {card.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Closing Text Block */}
          <div className="mt-20 text-center max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-slate-200 font-medium leading-relaxed">
              TFDI arbejder for at skabe varig forandring gennem støtte, oplysning, samarbejde og politisk fokus på området.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
